import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { MessageSquare, Send, Mic, X, Sparkles, Bot } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useUserRole } from "@/hooks/useUserRole";
import { useLeadCount } from "@/hooks/useLeadCount";
import { useCurrentLeadSheet } from "@/hooks/useCurrentLeadSheet";
import { cn } from "@/lib/utils";
import { TelegramSettings } from "@/components/TelegramSettings";
import { getCurrentUser } from "@/lib/authUser";
import { requestWithAuthRecovery } from "@/lib/authRequest";
import { useAuth } from "@/contexts/AuthContext";

interface Message {
  role: "user" | "assistant";
  content: string;
}

interface AIResult {
  leadId: string;
  leadName: string;
  action: 'created' | 'updated' | 'moved';
  hasGanttChanges: boolean;
}

interface FloatingAIChatProps {
  onLeadChange?: () => void;
  onNavigateToLead?: (result: AIResult) => void;
}

const TOOLTIP_DISMISSED_KEY = "ai-chat-tooltip-dismissed";

export function FloatingAIChat({ onLeadChange, onNavigateToLead }: FloatingAIChatProps) {
  
  const { workspace, selectedGroupId } = useWorkspace();
  const { initializing } = useAuth();
  const { isDemo } = useUserRole();
  const { count, canAddLead } = useLeadCount(10);
  const { sheet: currentSheet, fullLabel: sheetLabel } = useCurrentLeadSheet();
  const [isOpen, setIsOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [leadPanelWidth, setLeadPanelWidth] = useState(0);
  const [aiAssistantName, setAiAssistantName] = useState<string>("AI Assistant");
  const [messages, setMessages] = useState<Message[]>([
    { role: "assistant", content: "Hi! I can help you manage your leads and timelines. Tell me about a new lead, ask me to update existing ones, move leads between sheets, or ask questions about your data." }
  ]);
  const [input, setInput] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const [showTelegram, setShowTelegram] = useState(false);
  const [isGroupAdmin, setIsGroupAdmin] = useState(false);

  // Listen for lead panel open/close/resize events
  useEffect(() => {
    const handlePanelChange = (e: Event) => {
      const width = (e as CustomEvent<number>).detail ?? 0;
      setLeadPanelWidth(width);
    };
    window.addEventListener('lead-panel-width-change', handlePanelChange);
    return () => window.removeEventListener('lead-panel-width-change', handlePanelChange);
  }, []);

  // Show tooltip if not dismissed before
  useEffect(() => {
    const dismissed = localStorage.getItem(TOOLTIP_DISMISSED_KEY);
    if (!dismissed) {
      const timer = setTimeout(() => setShowTooltip(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const dismissTooltip = () => {
    setShowTooltip(false);
    localStorage.setItem(TOOLTIP_DISMISSED_KEY, "true");
  };

  const handleOpenChat = () => {
    setIsOpen(true);
    if (showTooltip) dismissTooltip();
  };

  // Fetch AI assistant name
  useEffect(() => {
    const fetchAIName = async () => {
      if (initializing) return;
      try {
        if (workspace === "groups" && selectedGroupId) {
          const { data, error } = await requestWithAuthRecovery(() =>
            supabase.from("groups").select("ai_assistant_name").eq("id", selectedGroupId).single(),
          );
          if (error) throw error;
          setAiAssistantName(data?.ai_assistant_name || "AI Assistant");
        } else {
          const user = await getCurrentUser();
          if (!user) return;
          const { data, error } = await requestWithAuthRecovery(() =>
            supabase.from("profiles").select("ai_assistant_name").eq("user_id", user.id).single(),
          );
          if (error) throw error;
          setAiAssistantName(data?.ai_assistant_name || "AI Assistant");
        }
      } catch (error) {
        console.error("Error fetching AI name:", error);
      }
    };
    fetchAIName();
  }, [workspace, selectedGroupId, initializing]);

  // Check if user is group admin for Telegram settings
  useEffect(() => {
    const checkAdmin = async () => {
      if (initializing) return;
      if (workspace !== "groups" || !selectedGroupId) {
        setIsGroupAdmin(false);
        return;
      }
      try {
        const user = await getCurrentUser();
        if (!user) return;
        const { data, error } = await requestWithAuthRecovery(() =>
          supabase
            .from("group_members")
            .select("role")
            .eq("group_id", selectedGroupId)
            .eq("user_id", user.id)
            .maybeSingle(),
        );
        if (error) {
          console.error("Error checking group admin:", error);
          return;
        }
        setIsGroupAdmin(data?.role === "leader" || data?.role === "admin");
      } catch (error) {
        console.error("Error checking group admin:", error);
      }
    };
    checkAdmin();
  }, [workspace, selectedGroupId, initializing]);

  const handleSend = async () => {
    if (!input.trim() || isProcessing) return;

    setIsProcessing(true);
    const userMessage: Message = { role: "user", content: input };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    const conversationText = input;
    setInput("");

    // Demo user lead limit check (only for likely creates)
    if (isDemo && workspace !== 'groups') {
      const user = await getCurrentUser();
      if (user) {
        const { count: currentCount } = await supabase
          .from("leads")
          .select("*", { count: "exact", head: true })
          .eq("user_id", user.id)
          .is("group_id", null);
        
        if (currentCount && currentCount >= 10) {
          setIsProcessing(false);
          const errorMessage: Message = {
            role: "assistant",
            content: `You've reached the 10 lead limit for demo users. Please upgrade to add more leads.`,
          };
          setMessages((prev) => [...prev, errorMessage]);
          toast.error("Lead limit reached", { description: `Demo users can only create up to 10 leads.` });
          return;
        }
      }
    }

    try {
      const FUNCTION_TIMEOUT = 65000;
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timed out. Please try again.')), FUNCTION_TIMEOUT)
      );

      // Send rich payload, no client-side intent detection
      const invokePromise = supabase.functions.invoke("process-lead", {
        body: {
          conversationText,
          groupId: workspace === 'groups' ? selectedGroupId : null,
          currentSheet: currentSheet || 'hot',
          conversationHistory: updatedMessages.slice(-10),
        },
      });

      const { data, error } = await Promise.race([invokePromise, timeoutPromise]) as any;
      if (error) throw error;

      setIsProcessing(false);

      const responseAction = data?.action;
      const responseMessage = data?.message || '';

      // Build response based on action type
      let displayContent = '';

      switch (responseAction) {
        case 'created':
          displayContent = responseMessage || `Added ${data.lead?.lead_name} to your leads.`;
          break;

        case 'updated': {
          const subAction = data.sub_action;
          const ganttResults = data.gantt_results;
          if (subAction === 'update_timeline' && ganttResults) {
            const parts = [];
            if (ganttResults.added > 0) parts.push(`${ganttResults.added} task${ganttResults.added > 1 ? 's' : ''} added`);
            if (ganttResults.updated > 0) parts.push(`${ganttResults.updated} task${ganttResults.updated > 1 ? 's' : ''} updated`);
            if (ganttResults.deleted > 0) parts.push(`${ganttResults.deleted} task${ganttResults.deleted > 1 ? 's' : ''} removed`);
            displayContent = responseMessage || `Updated ${data.lead?.lead_name}'s timeline: ${parts.join(', ')}.`;
          } else if (subAction === 'update_both') {
            displayContent = responseMessage || `Updated ${data.lead?.lead_name}'s info and timeline.`;
          } else {
            displayContent = responseMessage || `Updated ${data.lead?.lead_name}'s information.`;
          }
          if (data.confidence === 'fuzzy') {
            displayContent += ` (Matched by closest name)`;
          }
          break;
        }

        case 'moved':
          displayContent = responseMessage || `Moved lead successfully.`;
          if (data.confidence === 'fuzzy') displayContent += ` (Matched by closest name)`;
          break;

        case 'query': {
          const leads = data.leads || [];
          if (leads.length === 0) {
            displayContent = responseMessage || 'No matching leads found.';
          } else {
            const shown = leads.slice(0, 15);
            displayContent = (responseMessage || `Found ${leads.length} lead(s):`) + '\n\n' +
              shown.map((l: any) => {
                const bits = [l.status || 'new', `${l.priority || 'medium'} priority`];
                if (l.value) bits.push(`$${Number(l.value).toLocaleString()}`);
                if (l.next_action_date) bits.push(`next: ${l.next_action_date}`);
                return `• **${l.lead_name}**${l.company ? ` — ${l.company}` : ''} — ${bits.join(', ')}`;
              }).join('\n') +
              (leads.length > shown.length ? `\n\n…and ${leads.length - shown.length} more` : '');
          }
          break;
        }

        case 'get_details': {
          const lead = data.lead;
          if (!lead) {
            displayContent = responseMessage || "I couldn't load that lead.";
            break;
          }
          const lines: string[] = [];
          lines.push(`**${lead.lead_name}**${lead.company ? ` — ${lead.company}` : ''}`);
          const meta = [
            `Status: ${lead.status || 'new'}`,
            `Priority: ${lead.priority || 'medium'}`,
            `Sheet: ${data.sheet || 'hot'}`,
          ];
          if (lead.value) meta.push(`Value: $${Number(lead.value).toLocaleString()}`);
          lines.push(meta.join(' · '));
          if (lead.email || lead.phone) {
            lines.push([lead.email && `📧 ${lead.email}`, lead.phone && `📞 ${lead.phone}`].filter(Boolean).join('   '));
          }
          if (lead.source) lines.push(`Source: ${lead.source}`);
          if ((data.products || []).length) lines.push(`Products: ${data.products.join(', ')}`);
          if (lead.next_action) lines.push(`Next action: ${lead.next_action}${lead.next_action_date ? ` (${lead.next_action_date})` : ''}`);
          const upcoming = (data.gantt_items || []).filter((g: any) => g.upcoming).slice(0, 3);
          if (upcoming.length) {
            lines.push('');
            lines.push('📅 Upcoming:');
            upcoming.forEach((g: any) => {
              const when = g.is_all_day ? g.start_date : `${g.start_date}${g.start_time ? ' ' + g.start_time : ''}`;
              lines.push(`  • ${g.title} — ${when}`);
            });
          }
          if (lead.conversation_notes) {
            const snippet = String(lead.conversation_notes).slice(0, 240);
            lines.push('');
            lines.push(`📝 ${snippet}${lead.conversation_notes.length > 240 ? '…' : ''}`);
          }
          displayContent = lines.join('\n');
          break;
        }

        case 'bulk':
          displayContent = responseMessage || `Updated ${data.count || 0} lead(s).`;
          break;

        case 'create_product':
          displayContent = responseMessage || `Product created successfully.`;
          break;

        case 'delete_product':
          displayContent = responseMessage || `Product deleted successfully.`;
          break;

        case 'clarify': {
          const alternatives = data.alternatives || [];
          if (alternatives.length > 0) {
            displayContent = (responseMessage || 'Multiple leads match. Which one?') + '\n\n' +
              alternatives.map((name: string) => `• ${name}`).join('\n');
          } else {
            displayContent = responseMessage || "I couldn't find that lead. Could you clarify the name?";
          }
          break;
        }

        default:
          displayContent = responseMessage || "I understand. How can I help you with your leads?";
      }

      const aiMessage: Message = { role: "assistant", content: displayContent };
      setMessages((prev) => [...prev, aiMessage]);

      // Toast + navigation for actionable results
      if (data?.lead && ['created', 'updated', 'moved'].includes(responseAction)) {
        const toastLabel = responseAction === 'moved' ? 'Lead moved' : responseAction === 'updated' ? 'Lead updated' : 'Lead created';
        toast.success(toastLabel, {
          description: `${data.lead.lead_name}, ${responseAction}.${data.has_gantt_changes ? ' Timeline modified.' : ''}`,
        });
        onLeadChange?.();
        onNavigateToLead?.({
          leadId: data.lead.id,
          leadName: data.lead.lead_name,
          action: responseAction as 'created' | 'updated' | 'moved',
          hasGanttChanges: data.has_gantt_changes || false,
        });
      }

      if (responseAction === 'bulk' && data.count > 0) {
        toast.success("Bulk update", { description: `${data.count} lead(s) updated.` });
        onLeadChange?.();
      }

      if (responseAction === 'create_product') {
        toast.success("Product created", { description: data.product?.name || 'New product added.' });
        onLeadChange?.();
      }

      if (responseAction === 'delete_product') {
        toast.success("Product deleted", { description: responseMessage });
        onLeadChange?.();
      }

    } catch (error: any) {
      setIsProcessing(false);
      
      // Parse edge function error responses
      let errorContent = "Sorry, I encountered an error processing your request. Please try again.";
      let toastTitle = "Error";
      let toastDesc = error.message || "Failed to process your request";

      // Check for rate limit or payment errors from edge function
      const ctx = error?.context;
      const status = ctx?.status || ctx?.statusCode;
      const bodyText = typeof ctx?.body === 'string' ? ctx.body : '';
      
      if (status === 429 || error.message?.includes('rate limit') || error.message?.includes('Rate limit') || bodyText.includes('Rate limit')) {
        errorContent = "I'm getting a lot of requests right now. Please wait a moment and try again.";
        toastTitle = "Rate limit reached";
        toastDesc = "Too many requests. Please wait a minute before trying again.";
      } else if (status === 402 || error.message?.includes('Payment required') || bodyText.includes('Payment required')) {
        errorContent = "AI credits have been exhausted. Please add funds to continue using the AI assistant.";
        toastTitle = "Credits exhausted";
        toastDesc = "Please add AI credits in your workspace settings to continue.";
      } else if (status === 504 || error.message?.includes('timed out') || error.message?.includes('Request timed out')) {
        errorContent = "The request took too long. This usually happens during high traffic. Please try again.";
        toastTitle = "Request timed out";
        toastDesc = "The server is busy. Please try again in a moment.";
      }

      const errorMessage: Message = { role: "assistant", content: errorContent };
      setMessages((prev) => [...prev, errorMessage]);
      toast.error(toastTitle, { description: toastDesc });
    }
  };

  const handleVoiceInput = async () => {
    if (isRecording) {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
        mediaRecorderRef.current.stop();
      }
    } else {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const mediaRecorder = new MediaRecorder(stream);
        mediaRecorderRef.current = mediaRecorder;
        audioChunksRef.current = [];
        mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) audioChunksRef.current.push(event.data);
        };
        mediaRecorder.onstop = async () => {
          const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
          await transcribeAudio(audioBlob);
          stream.getTracks().forEach(track => track.stop());
        };
        mediaRecorder.start();
        setIsRecording(true);
        toast("Recording started", { description: "Speak now..." });
      } catch (error) {
        console.error('Error accessing microphone:', error);
        toast.error("Microphone access denied", { description: "Please allow microphone access to use voice input." });
      }
    }
  };

  const transcribeAudio = async (audioBlob: Blob) => {
    try {
      setIsRecording(false);
      toast("Transcribing...", { description: "Processing your audio..." });
      const reader = new FileReader();
      reader.readAsDataURL(audioBlob);
      reader.onloadend = async () => {
        const base64Audio = (reader.result as string).split(',')[1];
        const { data, error } = await supabase.functions.invoke('transcribe-audio', {
          body: { audio: base64Audio }
        });
        if (error) throw error;
        if (data?.text) {
          setInput(data.text);
          toast.success("Transcription complete", { description: "Your message has been transcribed." });
        }
      };
    } catch (error) {
      console.error('Transcription error:', error);
      toast.error("Transcription failed", { description: "Failed to transcribe audio. Please try again." });
    }
  };

  const recentMessages = messages.slice(-4);

  return (
    <>
      {/* Tooltip Bubble */}
      {showTooltip && !isOpen && (
        <div
          className="fixed bottom-24 md:bottom-24 z-50 animate-fade-in-up transition-[right] duration-300"
          style={{ right: `${leadPanelWidth + 24}px` }}
        >
          <div className="relative bg-card border rounded-xl shadow-lg p-3 pr-8 max-w-[220px]">
            <button
              onClick={dismissTooltip}
              className="absolute top-2 right-2 text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Dismiss"
            >
              <X className="h-3.5 w-3.5" />
            </button>
            <p className="text-sm text-foreground">
              <span className="font-medium">Need help?</span> Chat with your AI assistant here!
            </p>
            <div className="absolute -bottom-2 right-8 w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-card" />
            <div className="absolute -bottom-[9px] right-8 w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-border -z-10" />
          </div>
        </div>
      )}

      {/* Floating Button */}
      <Button
        onClick={handleOpenChat}
        className={cn(
          "fixed bottom-[calc(4rem+env(safe-area-inset-bottom)+16px)] md:bottom-6 z-50 h-14 w-14 md:h-16 md:w-16 rounded-full shadow-lg transition-all duration-300",
          "hover:scale-105 hover:shadow-xl",
          isOpen && "scale-0 opacity-0"
        )}
        style={{ right: `${leadPanelWidth + 24}px` }}
        size="icon"
      >
        <Sparkles className="h-6 w-6 md:h-7 md:w-7" />
      </Button>

      {/* Chat Panel */}
      <div
        className={cn(
          "fixed bottom-[calc(4rem+env(safe-area-inset-bottom)+12px)] md:bottom-6 z-50 w-[380px] max-w-[calc(100vw-3rem)] rounded-2xl border bg-background shadow-2xl transition-all duration-300",
          isOpen
            ? "translate-y-0 opacity-100 scale-100"
            : "translate-y-4 opacity-0 scale-95 pointer-events-none"
        )}
        style={{ right: `${leadPanelWidth + 24}px` }}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
              <MessageSquare className="h-4 w-4 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold text-sm">{aiAssistantName}</h3>
              <div className="flex items-center gap-1.5">
                <p className="text-xs text-muted-foreground">Lead & Timeline Assistant</p>
                {sheetLabel && (
                  <span className="text-[10px] bg-primary/10 text-primary px-1.5 py-0.5 rounded-full font-medium leading-none">
                    {sheetLabel}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {(workspace !== "groups" || selectedGroupId) && (
              <Button
                variant={showTelegram ? "secondary" : "ghost"}
                size="icon"
                className="h-8 w-8"
                onClick={() => setShowTelegram(!showTelegram)}
                title="Telegram Integration"
              >
                <Bot className="h-4 w-4" />
              </Button>
            )}
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setIsOpen(false); setShowTelegram(false); }}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Telegram Settings Panel */}
        {showTelegram && (workspace !== "groups" || selectedGroupId) && (
          <div className="border-b px-4 py-3 max-h-[200px] overflow-y-auto">
            {workspace === "groups" && selectedGroupId ? (
              <TelegramSettings groupId={selectedGroupId} isAdmin={isGroupAdmin} />
            ) : (
              <TelegramSettings groupId={null} isAdmin={true} />
            )}
          </div>
        )}

        {/* Messages */}
        <ScrollArea className={`${showTelegram && (workspace !== "groups" || selectedGroupId) ? "h-[80px]" : "h-[280px]"} px-4 py-3`}>
          <div className="space-y-3">
            {recentMessages.map((message, i) => (
              <div
                key={i}
                className={cn(
                  "text-sm p-3 rounded-xl max-w-[90%] whitespace-pre-wrap",
                  message.role === "user"
                    ? "bg-primary text-primary-foreground ml-auto"
                    : "bg-muted"
                )}
              >
                {message.content}
              </div>
            ))}
            {isProcessing && (
              <div className="bg-muted text-sm p-3 rounded-xl max-w-[90%]">
                <div className="flex items-center gap-2">
                  <div className="flex gap-1">
                    <span className="h-2 w-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="h-2 w-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="h-2 w-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                  <span className="text-muted-foreground">Processing...</span>
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Input */}
        <div className="border-t p-3">
          <div className="flex gap-2">
            <Textarea
              placeholder="Tell me about a lead, update info, or ask a question..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              className="min-h-[44px] max-h-[120px] resize-none rounded-xl"
              disabled={isProcessing}
            />
            <div className="flex flex-col gap-1 shrink-0">
              <Button
                onClick={handleVoiceInput}
                size="icon"
                variant={isRecording ? "destructive" : "outline"}
                disabled={isProcessing}
                className="h-[44px] w-10 rounded-xl"
              >
                <Mic className="h-4 w-4" />
              </Button>
            </div>
            <Button
              onClick={handleSend}
              size="icon"
              disabled={isProcessing || !input.trim()}
              className="h-[44px] w-10 rounded-xl"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
